import { Toggle } from '@amelio/ui-library';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import KeyResultProgressBar from './KeyResultProgressBar';
import ValueStepper from './ValueStepper';

import type { ObjectiveFormData } from '../validation';
import type { Control } from 'react-hook-form';

import { NumberInput } from '@/components/NumberInput/NumberInput';
import { VALUE_PILL, VALUE_PILL_INPUT } from '@/components/objectives/objectiveStyles';
import { isProgressComplete } from '@/utils/objectiveProgress';

interface KeyResultProgressRowProps {
    control: Control<ObjectiveFormData>;
    index: number;
    isYesNo: boolean;
    displayPercentage: number;
    onPercentageChange: (nextPercentage: number) => void;
    /** Brings a typed or stepped value back inside [start, target]. */
    clampValue: (value: number) => number;
    barLabel: string;
    /** The denominator of the value pill, already worded in the row's unit. */
    targetLabel: string;
}

/** Where the measurement stands today: the bar, then the control that writes the current value —
 * a yes/no switch or a number beside its target. */
export default function KeyResultProgressRow({
    control,
    index,
    isYesNo,
    displayPercentage,
    onPercentageChange,
    clampValue,
    barLabel,
    targetLabel,
}: KeyResultProgressRowProps) {
    const { t } = useTranslation();

    return (
        <div className={'flex flex-wrap items-center gap-4'}>
            <KeyResultProgressBar
                percentage={displayPercentage}
                onChange={onPercentageChange}
                complete={isProgressComplete(displayPercentage)}
                label={barLabel}
                className={'h-2.5 min-w-30'}
                dataTestId={`key-result-${index}-progress-bar`}
            />

            <Controller
                name={`keyResults.${index}.currentValue`}
                control={control}
                render={({ field }) =>
                    isYesNo ? (
                        <div
                            className={
                                'border-primary bg-surface-field flex items-center gap-3 rounded-full border px-4 py-2'
                            }
                        >
                            <Toggle
                                size={'md'}
                                checked={field.value === 1}
                                onChange={(checked) => field.onChange(checked ? 1 : 0)}
                                // The label doubles as the value here — "yes" or "no" — rather
                                // than being a static "current value" caption.
                                leftLabel={t(
                                    field.value === 1
                                        ? 'objectives.form.yes'
                                        : 'objectives.form.no',
                                )}
                                dataTestId={`key-result-${index}-current-toggle`}
                            />
                        </div>
                    ) : (
                        <div className={`${VALUE_PILL} gap-2.5 px-6 py-1`}>
                            <NumberInput
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={(value) => field.onChange(clampValue(value))}
                                placeholder={t('objectives.form.currentValue')}
                                ariaLabel={t('objectives.form.currentValue')}
                                className={`${VALUE_PILL_INPUT} w-17 text-[12px]`}
                                dataTestId={`key-result-${index}-current`}
                            />

                            <ValueStepper
                                onIncrease={() =>
                                    field.onChange(clampValue(Number(field.value ?? 0) + 1))
                                }
                                onDecrease={() =>
                                    field.onChange(clampValue(Number(field.value ?? 0) - 1))
                                }
                                dataTestIdPrefix={`key-result-${index}-current`}
                            />

                            <span className={'text-obj-muted text-[12px] font-medium'}>
                                {targetLabel}
                            </span>
                        </div>
                    )
                }
            />
        </div>
    );
}
